import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../css/StoreHeader.css";
import { fetchCustomer, fetchCart } from "./ShopifyClient";
import AuthModal from "./AuthModal";

type Customer = { firstName?: string; email?: string } | null;

export default function StoreHeader() {
  const [customer, setCustomer] = useState<Customer>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("customer_token");
    if (token) {
      fetchCustomer(token).then((c) => {
        if (c) setCustomer(c);
      });
    }

    // Load initial cart count
    updateCartCount();

    // Listen for cart updates
    const handleCartUpdate = () => {
      updateCartCount();
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  const updateCartCount = async () => {
    const cartId = localStorage.getItem("cart_id");
    if (cartId) {
      try {
        const cart = await fetchCart(cartId);
        setCartCount(cart?.totalQuantity || 0);
      } catch (error) {
        console.error("Error fetching cart:", error);
        setCartCount(0);
      }
    } else {
      setCartCount(0);
    }
  };

  const handleAuthSuccess = (authenticatedCustomer: Customer) => {
    setCustomer(authenticatedCustomer);
  };

  const handleUserIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!customer) {
      setShowAuthModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("customer_token");
    setCustomer(null);
  };

  const handleCartClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    const cartId = localStorage.getItem("cart_id");

    if (!cartId) {
      window.alert("Your cart is empty. Browse our wines and vouchers to add items.");
      return;
    }

    try {
      const cart = await fetchCart(cartId);
      if (!cart || cart.totalQuantity === 0) {
        window.alert("Your cart is empty. Browse our wines and vouchers to add items.");
      } else if (cart.checkoutUrl) {
        localStorage.removeItem("cart_id"); // prevent reprocessing
        window.location.href = cart.checkoutUrl;
      } else {
        window.alert("Unable to load cart. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      window.alert("Failed to load cart. Please try again.");
    }
  };

  return (
    <header className="store-header py-3 px-3">
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="logo-link">
          <img
            src="/images/christmas_logo.png"
            alt="Ely Wine Bar Logo"
            className="logo"
          />
        </Link>

        {/* Nav Links */}
        <nav className="navbar">
          <Link className="nav-link" to="/wine-tastings">
            Wine-Tasting
          </Link>
          <Link className="nav-link" to="/vouchers">
            Vouchers
          </Link>
        </nav>

        {/* User + Cart */}
        <div className="icons-container">
          <div className="dropdown">
            <button
              className="btn text-dark d-flex align-items-center gap-1"
              onClick={handleUserIconClick}
              style={{ background: "none", border: "none", padding: 0 }}
            >
              <i className="bi bi-person-circle fs-4"></i>
              {customer && (
                <span className="fw-bold">
                  {customer.firstName || customer.email}
                </span>
              )}
            </button>
            {customer && (
              <div
                className="dropdown-menu show position-absolute"
                style={{ top: "100%", right: 0 }}
              >
                <button className="dropdown-item" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>

          <button
            className="cart-icon text-dark position-relative"
            onClick={handleCartClick}
            style={{ background: "none", border: "none", padding: 0 }}
          >
            <i className="bi bi-cart fs-4"></i>
            {cartCount > 0 && (
              <span className="badge rounded-pill bg-danger cart-badge">
                {cartCount}
                <span className="visually-hidden">items in cart</span>
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </header>
  );
}
